export const UNAVAILABLE_STATISTICS_CALCULATORS = [
  "Prior Posterior",
  "Bayes Factor",
  "MCMC",
  "Bootstrap",
  "Monte Carlo",
  "Random Number",
  "Information Gain",
  "Mutual Information",
  "KL Divergence",
  "Cross Entropy",
  "Survival Analysis",
  "Kaplan Meier",
] as const;

type UnavailableStatisticsCalculator =
  (typeof UNAVAILABLE_STATISTICS_CALCULATORS)[number];

const unavailableTitles = new Set<string>(UNAVAILABLE_STATISTICS_CALCULATORS);

const unavailableRelatedCalculators: Record<
  UnavailableStatisticsCalculator,
  { slug: string; context: string }
> = {
  "Prior Posterior": {
    slug: "statistics-bayesian-inference-calculator",
    context: "a supported binary-hypothesis Bayesian calculation",
  },
  "Bayes Factor": {
    slug: "statistics-bayesian-inference-calculator",
    context: "a supported binary-hypothesis Bayesian calculation",
  },
  MCMC: {
    slug: "statistics-bayesian-inference-calculator",
    context: "a supported binary-hypothesis Bayesian calculation",
  },
  Bootstrap: {
    slug: "statistics-mean-calculator",
    context: "a basic descriptive summary",
  },
  "Monte Carlo": {
    slug: "statistics-probability-calculator",
    context: "a supported exact probability model",
  },
  "Random Number": {
    slug: "statistics-probability-calculator",
    context: "a supported exact probability model",
  },
  "Information Gain": {
    slug: "statistics-probability-calculator",
    context: "a supported exact probability model",
  },
  "Mutual Information": {
    slug: "statistics-probability-calculator",
    context: "a supported exact probability model",
  },
  "KL Divergence": {
    slug: "statistics-probability-calculator",
    context: "a supported exact probability model",
  },
  "Cross Entropy": {
    slug: "statistics-probability-calculator",
    context: "a supported exact probability model",
  },
  "Survival Analysis": {
    slug: "statistics-confidence-interval-calculator",
    context: "a supported confidence-interval estimate",
  },
  "Kaplan Meier": {
    slug: "statistics-confidence-interval-calculator",
    context: "a supported confidence-interval estimate",
  },
};

const activeStatisticsDescriptions: Record<string, string> = {
  "Poisson Distribution":
    "Calculate P(X = k), P(X ≤ k), and P(X ≥ k) with a Poisson count model, alongside its mean, variance, and standard deviation. Assumes independent events at a constant average rate.",
  "Bayesian Inference":
    "Apply Bayes' theorem to a binary hypothesis using a prior probability, sensitivity, and false-positive rate; view the evidence probability, posterior probability, and change from the prior.",
};

export function getStatisticsCalculatorTitle(title: string): string {
  return title.replace(/\s+calculator$/i, "").trim();
}

export function getStatisticsUnavailableCalculatorProfile(title: string) {
  const topic = getStatisticsCalculatorTitle(title);
  if (!unavailableTitles.has(topic)) return null;
  const related = unavailableRelatedCalculators[topic as UnavailableStatisticsCalculator];
  const notice = `This calculator is not available yet. We're building a dedicated version of the ${topic} calculator.`;
  return {
    topic,
    notice,
    seoDescription: `${notice} No input fields or calculated results are shown on this page yet.`,
    includeInteractiveSchemas: false as const,
    related,
  };
}

export function getStatisticsCalculatorDescription(title: string): string | undefined {
  const topic = getStatisticsCalculatorTitle(title);
  const unavailable = getStatisticsUnavailableCalculatorProfile(topic);
  if (unavailable) return unavailable.seoDescription;
  return activeStatisticsDescriptions[topic];
}

export function getStatisticsUnavailableRelatedCalculator(title: string) {
  return getStatisticsUnavailableCalculatorProfile(title)?.related ?? null;
}

export function getStatisticsUnavailableFaqItems(title: string) {
  const profile = getStatisticsUnavailableCalculatorProfile(title);
  if (!profile) return null;
  return [
    {
      question: `Is the ${profile.topic} calculator available?`,
      answer: profile.notice,
    },
    {
      question: `Does this page calculate ${profile.topic}?`,
      answer:
        "No. It currently has no input fields and displays no calculated results.",
    },
  ];
}